import { Reveal, StaggerGroup, StaggerItem } from "@/components/Reveal";
import { PrimaryCTA } from "@/components/CTA";
import { Tick } from "@/components/Annotation";
import { ROUTES } from "@/config/links";
import { useLang } from "@/i18n";

/**
 * Section 7 — Coaching plan / practical details.
 * No public price, no fixed session count — the plan and fee are agreed
 * during the free consultation, as specified.
 */
export const CoachingPlan = () => {
  const { t } = useLang();
  const items = t("landing.plan_items");
  return (
    <section id="plan" className="relative z-10 py-24 md:py-36 bg-paper-deep/40" data-testid="coaching-plan-section">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="grid lg:grid-cols-12 gap-8 items-end mb-14 md:mb-20">
          <Reveal className="lg:col-span-7">
            <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-5">
              {t("landing.plan_eyebrow")}
            </p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-ink">
              {t("landing.plan_h")}
            </h2>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-5">
            <p className="hidden md:block font-body text-ink-soft leading-relaxed" data-testid="plan-body-desktop">
              {t("landing.plan_body")}
            </p>
            <p className="md:hidden font-body text-ink-soft leading-relaxed" data-testid="plan-body-mobile">
              {t("landing.plan_body_mobile")}
            </p>
          </Reveal>
        </div>

        {/* Mobile-only compact summary line */}
        <p className="md:hidden font-hand text-lg text-terracotta mb-8" data-testid="plan-mobile-summary">
          {t("landing.plan_mobile_summary")}
        </p>

        {/* Desktop: 4 practical items */}
        <StaggerGroup className="hidden md:grid md:grid-cols-2 gap-x-10 gap-y-8 mb-14 md:mb-16">
          {Array.isArray(items) &&
            items.map((item, i) => (
              <StaggerItem key={i} className="flex gap-4">
                <Tick className="w-6 h-6 mt-1 shrink-0" />
                <div>
                  <h3 className="font-serif text-xl text-ink mb-1">{item.title}</h3>
                  <p className="font-body text-ink-soft leading-relaxed">{item.body}</p>
                </div>
              </StaggerItem>
            ))}
        </StaggerGroup>

        {/* Mobile: what to bring / between sessions */}
        <div className="md:hidden flex flex-col gap-6 mb-10">
          <div>
            <p className="font-body text-xs uppercase tracking-[0.22em] text-terracotta mb-2">
              {t("landing.plan_mobile_bring_label")}
            </p>
            <p className="font-body text-ink-soft leading-relaxed">{t("landing.plan_mobile_bring")}</p>
          </div>
          <div>
            <p className="font-body text-xs uppercase tracking-[0.22em] text-terracotta mb-2">
              {t("landing.plan_mobile_between_label")}
            </p>
            <p className="font-body text-ink-soft leading-relaxed">{t("landing.plan_mobile_between")}</p>
          </div>
        </div>

        <Reveal className="max-w-2xl border-l-2 border-terracotta pl-6 mb-12">
          <h3 className="font-serif text-xl md:text-2xl text-ink mb-3">{t("landing.plan_sub_h")}</h3>
          <p className="hidden md:block font-body text-ink-soft leading-relaxed" data-testid="plan-investment-desktop">
            {t("landing.plan_investment")}
          </p>
          <p className="md:hidden font-body text-ink-soft leading-relaxed" data-testid="plan-investment-mobile">
            {t("landing.plan_investment_mobile")}
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <PrimaryCTA href={ROUTES.bookPresentation} testId="plan-book-cta" location="coaching_plan" targetAssetId="booking_presentation">
            {t("landing.hero_book_cta")}
          </PrimaryCTA>
          <p className="hidden md:block mt-4 font-body text-sm text-ink-soft max-w-sm" data-testid="plan-microcopy-desktop">
            {t("landing.plan_microcopy")}
          </p>
          <p className="md:hidden mt-3 font-body text-sm text-ink-soft" data-testid="plan-microcopy-mobile">
            {t("landing.plan_microcopy_mobile")}
          </p>
        </Reveal>
      </div>
    </section>
  );
};
